import { Toaster } from "sonner";
import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import { auth, db } from "./firebase";
import { bootstrapCompanyOwnership } from "./services/companySettingsService";
import { usePanelTheme } from "./context/themeContext";
import { tData } from "./i18n";

import DesktopSidebar from "./components/DesktopSidebar";
import Header from "./components/Header";
import MobileSidebar from "./components/MobileSidebar";
import MobileBottomNav from "./components/MobileBottomNav";

import Dashboard from "./pages/Dashboard";
import Income from "./pages/Income";
import Expenses from "./pages/Expenses";
import Payments from "./pages/Payments";
import Materials from "./pages/Materials";
import Workers from "./pages/Workers";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Invoice from "./pages/Invoice";
import Clients from "./pages/Clients";
import Projects from "./pages/Projects";
import Offers from "./pages/Offers";
import Calendar from "./pages/Calendar";
import Timesheets from "./pages/Timesheets";
import ProjectProfit from "./pages/ProjectProfit";
import Kalkulation from "./pages/Kalkulation";

export default function App() {
  const { currentTheme, theme } = usePanelTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [lang, setLang] = useState(localStorage.getItem("bauheld_language") || "sq");
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(undefined);
  const [company, setCompany] = useState({
    companyName: "",
    logoUrl: "",
  });

  const t = tData[lang] || tData.sq;

  useEffect(() => {
    localStorage.setItem("bauheld_language", lang);
  }, [lang]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        await bootstrapCompanyOwnership();
        const snap = await getDoc(doc(db, "settings", "company"));
        if (snap.exists()) {
          const data = snap.data();
          setCompany({
            companyName: data.companyName || "",
            logoUrl: data.logoUrl || "",
          });
        } else {
          setCompany({
            companyName: "",
            logoUrl: "",
          });
        }
      } catch (error) {
        console.error("Error loading company settings:", error);
      }
    };

    if (user) {
      loadCompany();
    }
  }, [user]);

  useEffect(() => {
    const pageName = company.companyName || "BauHeld";
    document.title = `${pageName} · ${t.appName || "BauHeld"}`;
  }, [company.companyName, t.appName]);

  const pathToKey = (pathname) => {
    if (pathname === "/" || pathname === "/dashboard") return "dashboard";
    if (pathname === "/clients") return "clients";
    if (pathname === "/projects") return "projects";
    if (pathname === "/offers") return "offers";
    if (pathname === "/kalkulation") return "kalkulation";
    if (pathname === "/calendar") return "calendar";
    if (pathname === "/timesheets") return "timesheets";
    if (pathname === "/project-profit") return "projectProfit";
    if (pathname === "/invoice") return "invoice";
    if (pathname === "/income") return "income";
    if (pathname === "/payments") return "payments";
    if (pathname === "/expenses") return "expenses";
    if (pathname === "/materials") return "materials";
    if (pathname === "/workers") return "workers";
    if (pathname === "/reports") return "reports";
    if (pathname === "/settings") return "settings";
    return "dashboard";
  };

  const keyToPath = (key) => {
    switch (key) {
      case "dashboard":
        return "/dashboard";
      case "clients":
        return "/clients";
      case "projects":
        return "/projects";
      case "offers":
        return "/offers";
      case "kalkulation":
        return "/kalkulation";
      case "calendar":
        return "/calendar";
      case "timesheets":
        return "/timesheets";
      case "projectProfit":
        return "/project-profit";
      case "invoice":
        return "/invoice";
      case "income":
        return "/income";
      case "payments":
        return "/payments";
      case "expenses":
        return "/expenses";
      case "materials":
        return "/materials";
      case "workers":
        return "/workers";
      case "reports":
        return "/reports";
      case "settings":
        return "/settings";
      default:
        return "/dashboard";
    }
  };

  const active = pathToKey(location.pathname);

  const setActive = (key) => {
    navigate(keyToPath(key));
    setMenuOpen(false);
  };

  const menu = useMemo(
    () => [
      { key: "dashboard", label: t.dashboard || "Dashboard" },
      { key: "clients", label: t.clients || (lang === "de" ? "Kunden" : "Klientët") },
      { key: "projects", label: t.projects || (lang === "de" ? "Projekte" : "Projektet") },
      { key: "offers", label: t.offers || (lang === "de" ? "Angebote" : "Ofertat") },
      { key: "kalkulation", label: t.kalkulation || (lang === "de" ? "Kalkulation" : "Kalkulimi") },
      { key: "calendar", label: t.calendar || (lang === "de" ? "Kalender" : "Kalendari") },
      { key: "timesheets", label: t.timesheets || (lang === "de" ? "Arbeitszeiten" : "Orët e punës") },
      { key: "projectProfit", label: t.projectProfit || (lang === "de" ? "Projektgewinn" : "Fitimi i projekteve") },
      { key: "invoice", label: t.invoiceTitle || (lang === "de" ? "Rechnungen" : "Faturat") },
      { key: "income", label: t.income || "Hyrje" },
      { key: "payments", label: t.payments || (lang === "de" ? "Zahlungen" : "Pagesat") },
      { key: "expenses", label: t.expenses || (lang === "de" ? "Ausgaben" : "Shpenzimet") },
      { key: "materials", label: t.materials || (lang === "de" ? "Materialien" : "Materialet") },
      { key: "workers", label: t.workers || (lang === "de" ? "Mitarbeiter" : "Punëtorët") },
      { key: "reports", label: t.reports || (lang === "de" ? "Berichte" : "Raportet") },
      { key: "settings", label: t.settings || (lang === "de" ? "Einstellungen" : "Konfigurimet") },
    ],
    [t, lang]
  );

  if (user === undefined) {
    return null;
  }

  if (!user) {
    return <Login t={t} lang={lang} setLang={setLang} />;
  }

  return (
    <div className={`min-h-screen ${currentTheme.page}`}>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-20 top-0 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-cyan-400/10 blur-3xl" />
        <div
          className={`absolute bottom-0 left-1/3 h-80 w-80 rounded-full blur-3xl ${
            theme === "dark" ? "bg-indigo-500/10" : "bg-slate-900/5"
          }`}
        />
      </div>

      <div className="relative flex min-h-screen">
        <DesktopSidebar
          menu={menu}
          active={active}
          setActive={setActive}
          t={t}
          lang={lang}
          setLang={setLang}
          company={company}
        />

        <MobileSidebar
          open={menuOpen}
          setOpen={setMenuOpen}
          menu={menu}
          active={active}
          setActive={setActive}
          t={t}
          setLang={setLang}
          lang={lang}
          company={company}
        />

        <Toaster
          position="top-right"
          richColors
          closeButton
          expand={false}
          duration={3500}
        />

        <main className="flex-1 min-w-0 lg:pl-2">
          <div className="mx-auto w-full max-w-[1800px]">
            <Header
              title={menu.find((m) => m.key === active)?.label || t.dashboard}
              onMenu={() => setMenuOpen(true)}
              lang={lang}
              setLang={setLang}
              t={t}
            />

            <div className="px-3 pb-24 pt-3 md:px-6 md:pb-6 md:pt-4 xl:px-8">
              <div
                className={`rounded-[30px] border p-3 shadow-[0_30px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl md:rounded-[36px] md:p-4 ${
                  theme === "dark"
                    ? "border-slate-800/80 bg-slate-950/60"
                    : "border-white/60 bg-white/70"
                }`}
              >
                <div className="rounded-[24px] md:rounded-[28px]">
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard language={lang} />} />
                    <Route path="/clients" element={<Clients language={lang} />} />
                    <Route path="/projects" element={<Projects lang={lang} t={t} />} />
                    <Route path="/offers" element={<Offers lang={lang} t={t} />} />
                    <Route path="/kalkulation" element={<Kalkulation lang={lang} t={t} />} />
                    <Route path="/calendar" element={<Calendar lang={lang} t={t} />} />
                    <Route path="/timesheets" element={<Timesheets lang={lang} t={t} />} />
                    <Route path="/project-profit" element={<ProjectProfit lang={lang} t={t} />} />
                    <Route path="/invoice" element={<Invoice lang={lang} t={t} />} />
                    <Route path="/income" element={<Income lang={lang} t={t} />} />
                    <Route path="/expenses" element={<Expenses lang={lang} t={t} />} />
                    <Route path="/payments" element={<Payments lang={lang} t={t} />} />
                    <Route path="/materials" element={<Materials lang={lang} t={t} />} />
                    <Route path="/workers" element={<Workers lang={lang} t={t} />} />
                    <Route path="/reports" element={<Reports lang={lang} t={t} />} />
                    <Route
                      path="/settings"
                      element={<Settings language={lang} setLanguage={setLang} />}
                    />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </div>
              </div>
            </div>
          </div>
        </main>

        <MobileBottomNav activeTab={active} onChange={setActive} t={t} />
      </div>
    </div>
  );
}